// ─────────────────────────────────────────────
//  relief — staged relief on a room's assignment row.
//
//  Relief is a HANDOFF, not a break: the named CRNA and/or MD take the room
//  over for the rest of the day. It is staged first (crnaReliefId /
//  mdReliefId on the day's assignment row) and confirmed later — the card
//  reads "pending" in between, so the board shows a handoff that is agreed
//  but not yet done.
//
//  These helpers are PURE. They exist because the web card and the mobile
//  card both stage and both confirm; a second hand-written copy of the
//  takeover patch is how the two shells drift apart (the field list is
//  long and half of it is clears, which is exactly what gets forgotten).
// ─────────────────────────────────────────────

// A relief is staged on this row (either seat, or both).
export function hasPendingRelief(assignment) {
  return !!(assignment?.crnaReliefId || assignment?.mdReliefId);
}

// The names as the cards say them: "Relief X · MD Y — pending".
export function reliefSummary(assignment) {
  const crna = assignment?.crnaReliefName || "";
  const md = assignment?.mdReliefName || "";
  if (!crna && !md) return "";
  return `Relief ${crna || md}${crna && md ? ` · MD ${md}` : ""} — pending`;
}

// Confirm the takeover: the staged relief person(s) BECOME the room's primary
// CRNA / MD and the relief slot is cleared, freeing the person who left.
//
// Each seat is independent — confirming a CRNA-only relief must not touch the
// attending, so the patch only ever names the seats that were actually staged.
// The incoming CRNA's shift fields are cleared rather than inherited: a shift
// code belongs to the person who was rostered for it, and carrying the
// departing provider's code forward would make the new person inherit a
// departure time they never agreed to.
//
// Returns null when there is nothing staged — callers treat null as "no-op"
// so a double-tap can't write an empty patch.
export function takeoverPatch(assignment) {
  if (!hasPendingRelief(assignment)) return null;
  const patch = {};
  if (assignment.crnaReliefId) {
    patch.ahpId = assignment.crnaReliefId;
    patch.ahpName = assignment.crnaReliefName || "";
    patch.ahpShiftCode = null; // the new person's shift is set fresh on the grid
    patch.ahpDepartTime = "";
    patch.crnaReliefId = null;
    patch.crnaReliefName = "";
  }
  if (assignment.mdReliefId) {
    patch.attendingId = assignment.mdReliefId;
    patch.attendingName = assignment.mdReliefName || "";
    patch.mdReliefId = null;
    patch.mdReliefName = "";
  }
  return patch;
}

// Staging patch for a plain (non-solo) relief. `crna` / `md` are roster
// entries or null/undefined for "no relief in that seat".
//
// Clearing BOTH seats is a legitimate save (the handoff was called off), so
// this never guards on emptiness — it writes the nulls that undo the staging.
// A room that was solo stops being solo the moment a normal relief is staged.
export function stageReliefPatch({ assignment, crna, md }) {
  return {
    crnaReliefId: crna?.id || null,
    crnaReliefName: crna?.name || "",
    mdReliefId: md?.id || null,
    mdReliefName: md?.name || "",
    ...(assignment?.soloMd ? { soloMd: false } : {}),
  };
}
